import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, Eye, Moon, AppWindow, ArrowRight } from 'lucide-react';
import { supabase } from '../../utilities/supabase';
import { UserProfilePicture } from '../UserProfilePicture';
import LiveCursors from './LiveCursors';
import { setRecordPlace } from '../navigation/onlinePlace';

const MAX_SHOWN = 3;
// No mouse, keyboard, scroll or touch for this long → "Idle"
const IDLE_AFTER_MS = 3 * 60 * 1000;
// A refresh (or a quick hop away and back) within this long keeps the original "opened" time
const RESUME_WITHIN_MS = 2 * 60 * 1000;

export const STATES = {
  active: { dot: 'bg-green-500', text: 'Active now', Icon: Eye, dim: false },
  idle: { dot: 'bg-amber-400', text: 'Idle', Icon: Moon, dim: false },
  away: { dot: 'bg-base-300', text: 'In another tab', Icon: AppWindow, dim: true }
};
export const ORDER = { active: 0, idle: 1, away: 2 };
export const byState = (a, b) => (ORDER[a.state] ?? 0) - (ORDER[b.state] ?? 0);
const bySince = (a, b) => (a.since || '').localeCompare(b.since || '');

export const minutesBetween = (from, to) =>
  Math.max(0, Math.floor((to - new Date(from).getTime()) / 60000));

export const duration = minutes => {
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} hr${rest ? ` ${rest} min` : ''}`;
};

export const timeOfDay = at =>
  new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// When this person first opened the record in this browser tab (kept across a refresh)
const openedAt = storeKey => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
    if (saved && (!saved.leftAt || Date.now() - saved.leftAt < RESUME_WITHIN_MS)) {
      sessionStorage.setItem(storeKey, JSON.stringify({ since: saved.since }));
      return saved.since;
    }
  } catch {
    // storage blocked: just time from now
  }
  const since = new Date().toISOString();
  try {
    sessionStorage.setItem(storeKey, JSON.stringify({ since }));
  } catch {
    // ignore
  }
  return since;
};

const markLeft = storeKey => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
    if (saved) sessionStorage.setItem(storeKey, JSON.stringify({ ...saved, leftAt: Date.now() }));
  } catch {
    // ignore
  }
};

/** active / idle / away for this browser tab, with when that state began */
export const useMyState = () => {
  const [mine, setMine] = useState(() => ({
    state: document.visibilityState === 'hidden' ? 'away' : 'active',
    at: new Date().toISOString()
  }));
  const current = useRef(mine.state);

  useEffect(() => {
    let idleTimer;
    const set = next => {
      if (current.current === next) return;
      current.current = next;
      setMine({ state: next, at: new Date().toISOString() });
    };
    const armIdle = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => set('idle'), IDLE_AFTER_MS);
    };
    const onActivity = () => {
      if (document.visibilityState === 'hidden') return;
      set('active');
      armIdle();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(idleTimer);
        set('away');
      } else onActivity();
    };

    const events = ['mousemove', 'keydown', 'scroll', 'pointerdown', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onActivity);
    armIdle();
    return () => {
      clearTimeout(idleTimer);
      events.forEach(e => window.removeEventListener(e, onActivity, { capture: true }));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onActivity);
    };
  }, []);

  return mine;
};

/**
 * Who has this record open right now (Supabase Realtime presence), the viewer included.
 * Each open tab joins `viewers:<entityType>:<entityId>` and shares who they are, when they
 * opened it, whether they're active / idle / in another tab, and which record tab they're on.
 * People are de-duplicated by profile id: their most active tab, timed from the earliest open.
 */
const RecordViewers = ({ entityType, entityId, userProfile, viewingTab, recordLabel }) => {
  const [viewers, setViewers] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  // Where on the record this tab is (record tab + inner tab), so others can jump to it
  const path = `${location.pathname}${location.search}`;
  const [now, setNow] = useState(() => Date.now());
  const me = userProfile?.id;
  const mine = useMyState();
  const channelRef = useRef(null);
  const meta = useRef(null);
  const onCursor = useRef(null);

  const storeKey = `record-viewing:${entityType}:${entityId}`;

  // Join the record's channel
  useEffect(() => {
    if (!entityType || !entityId || !me) return undefined;

    meta.current = {
      id: me,
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      photo: userProfile.profilePhoto || null,
      since: openedAt(storeKey),
      state: mine.state,
      stateSince: mine.at,
      tab: viewingTab || null,
      path
    };

    const channel = supabase.channel(`viewers:${entityType}:${entityId}`, {
      config: { presence: { key: String(me) } }
    });
    channelRef.current = channel;

    const sync = () => {
      const people = Object.values(channel.presenceState())
        .filter(metas => metas.length)
        .map(metas => ({
          ...[...metas].sort((a, b) => byState(a, b) || bySince(a, b))[0],
          since: metas.map(m => m.since).sort()[0]
        }))
        .sort(bySince);
      setViewers(people);
    };

    channel
      .on('presence', { event: 'sync' }, sync)
      .on('broadcast', { event: 'cursor' }, ({ payload }) => onCursor.current?.(payload))
      .subscribe(status => {
      if (status === 'SUBSCRIBED') channel.track(meta.current);
    });

    return () => {
      markLeft(storeKey);
      channelRef.current = null;
      supabase.removeChannel(channel);
    };
    // Profile details only matter when we first join
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, me]);

  // Share state / tab changes with everyone else on the record
  useEffect(() => {
    if (!meta.current) return;
    meta.current = { ...meta.current, state: mine.state, stateSince: mine.at, tab: viewingTab || null, path };
    if (channelRef.current?.state === 'joined') channelRef.current.track(meta.current);
  }, [mine.state, mine.at, viewingTab, path]);

  // ONLINE_NOW_V1: the header's Active Now names this record (and tab) for everyone
  useEffect(() => {
    if (!recordLabel) return undefined;
    setRecordPlace(viewingTab ? `${recordLabel} · ${viewingTab}` : recordLabel);
    return () => setRecordPlace(null);
  }, [recordLabel, viewingTab]);

  // Keep the times fresh while the record is open
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  if (!viewers.length) return null;

  const isMe = v => String(v.id) === String(me);
  // You first, then others: active before idle before away, then by who opened it first
  const listed = [...viewers.filter(isMe), ...viewers.filter(v => !isMe(v)).sort(byState)];
  const shown = listed.slice(0, MAX_SHOWN);
  const extra = listed.length - MAX_SHOWN;
  const activeCount = listed.filter(v => (v.state || 'active') === 'active').length;
  // Someone else somewhere else on the record: clicking them takes you there
  const goTo = v => (!isMe(v) && v.path && v.path !== path ? () => navigate(v.path) : null);

  return (
    <div className='relative group flex items-center mr-2 cursor-default'>
      <LiveCursors
        channelRef={channelRef}
        onCursor={onCursor}
        me={me}
        others={listed.filter(v => !isMe(v))}
        viewingTab={viewingTab}
      />
      {/* Hover card: fades and drops in under the avatars */}
      <div className='absolute right-0 top-full z-50 pt-2.5 opacity-0 invisible translate-y-1 transition-all duration-150 ease-out group-hover:opacity-100 group-hover:visible group-hover:translate-y-0'>
        <div className='relative w-80 rounded-2xl bg-white ring-1 ring-base-900/5 shadow-xl shadow-base-900/10'>
          <span className='absolute -top-1.5 right-3 h-3 w-3 rotate-45 bg-white ring-1 ring-base-900/5 [clip-path:polygon(0_0,100%_0,0_100%)]' />
          <div className='flex items-center justify-between px-4 pt-3.5 pb-2.5'>
            <div className='flex items-center gap-2'>
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-green-500' />
              </span>
              <span className='text-[13px] font-semibold text-base-900'>On this record</span>
            </div>
            <span className='rounded-full bg-base-100 px-2 py-0.5 text-[11px] font-medium text-base-600'>
              {listed.length === 1 ? 'Just you' : `${listed.length} people · ${activeCount} active`}
            </span>
          </div>
          <ul className='px-2 pb-2'>
            {listed.map(v => {
              const state = v.state || 'active';
              const look = STATES[state] || STATES.active;
              const inState = v.stateSince ? minutesBetween(v.stateSince, now) : 0;
              const go = goTo(v);
              return (
                <li
                  key={v.id}
                  onClick={go || undefined}
                  title={go ? `Go to ${v.tab || 'where they are'}` : undefined}
                  className={`group/row flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-base-50 ${go ? 'cursor-pointer' : ''}`}
                >
                  <div className='relative flex-none h-10 w-10 leading-none'>
                    <UserProfilePicture
                      firstName={v.firstName}
                      lastName={v.lastName}
                      profilePicture={v.photo}
                      size='xl'
                      className={`rounded-full ring-1 ring-base-200 ${look.dim ? 'opacity-50 grayscale' : ''}`}
                    />
                    <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-white ${look.dot}`} />
                  </div>
                  <div className='flex min-w-0 flex-1 flex-col'>
                    <div className='flex items-center gap-1.5 min-w-0'>
                      <span className='truncate text-[13px] font-medium text-base-900'>
                        {`${v.firstName} ${v.lastName}`.trim() || 'Someone'}
                      </span>
                      {isMe(v) && (
                        <span className='flex-none rounded-full bg-brand-50 px-1.5 py-px text-[10px] font-medium text-brand-700'>
                          You
                        </span>
                      )}
                    </div>
                    <span className='mt-0.5 flex items-center gap-1 text-[11px] text-base-600 min-w-0'>
                      <look.Icon size={11} strokeWidth={2} className='flex-none text-base-400' />
                      <span className='truncate'>
                        {look.text}
                        {state !== 'active' && inState >= 1 && ` for ${duration(inState)}`}
                        {v.tab && state !== 'away' && (
                          <>
                            <span className='text-base-300'> · </span>
                            on {v.tab}
                          </>
                        )}
                      </span>
                    </span>
                    <span className='mt-0.5 flex items-center gap-1 text-[11px] text-base-400'>
                      <Clock size={11} strokeWidth={2} className='flex-none' />
                      Opened {timeOfDay(v.since)}
                      <span className='text-base-300'>·</span>
                      {duration(minutesBetween(v.since, now))}
                    </span>
                  </div>
                  {go && (
                    <ArrowRight
                      size={14}
                      strokeWidth={2}
                      className='flex-none text-base-300 transition-all group-hover/row:translate-x-0.5 group-hover/row:text-base-600'
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div className='flex -space-x-1.5 rounded-full p-0.5 transition-colors group-hover:bg-base-100'>
        {shown.map(v => (
          <div
            key={v.id}
            onClick={goTo(v) || undefined}
            title={goTo(v) ? `Go to ${v.tab || 'where they are'}` : undefined}
            className={`relative h-6 w-6 leading-none ${goTo(v) ? 'cursor-pointer' : ''}`}
          >
            <UserProfilePicture
              firstName={v.firstName}
              lastName={v.lastName}
              profilePicture={v.photo}
              size='md'
              className={`ring-2 ring-white rounded-full ${STATES[v.state]?.dim ? 'opacity-50 grayscale' : ''}`}
            />
            <span
              className={`absolute bottom-0 right-0 h-2 w-2 rounded-full ring-[1.5px] ring-white ${(STATES[v.state] || STATES.active).dot}`}
            />
          </div>
        ))}
        {extra > 0 && (
          <div className='h-6 w-6 rounded-full bg-base-100 ring-2 ring-white flex items-center justify-center text-[11px] font-medium text-base-600'>
            +{extra}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordViewers;
